import React, {
    useCallback,
    useContext, useEffect, useRef, useState,
} from "react";
import Link from "helpers/link";
import { PatioContext } from "@root/context/patio-calculator";
import InputField from "../../_components/input-field";
import MeasurementsWidget from "@root/components/patio-configurator/measurements-widget";
import Button from "@_components/button";
import { ReactComponent as CloseIcon } from "assets/svgs/close.svg";
import { ReactComponent as ArrowRightIcon } from "assets/svgs/arrow-right.svg";
import { MEASUREMENTS_STATE } from "@root/constants/enums";
import infoIcon from "assets/svgs/info.svg";
import { FormValidateContext } from "@root/context/form-validate";
import { CURRENCY } from "utils/constants";
import Money from "components/money";

export const MeasureInput = ({ data, className, measurementIndex }) => {
    const { activeCoordinate, setActiveCoordinate, configuration, setMeasurement, measurements: measurementsValues } = useContext(PatioContext) || {};
    const { setGlobalError } = useContext(FormValidateContext) || {};
    const { key, name, value, default: defaultValue, minimum } = data;
    const inputRef = useRef(null);

    function currentMeasurement() {
        return measurementsValues[key] || {};
    }
    const handleChange = (event) => {
        const rawValue = event.target.value;
        const numericValue = +rawValue === 0 ? 0 : +rawValue;
        setMeasurement(key, numericValue);
        setGlobalError(null);
    };

    const indexToLetter = (index) => String.fromCharCode(65 + index);

    // focus field when activeCoordinate is equal to the key
    useEffect(() => {
        if (activeCoordinate === key) {
            // find the input field in ref and focus it
            inputRef.current.querySelector("input").focus();
        }
    }, [activeCoordinate]);

    let customValidation = useCallback((value) => {
        if (currentMeasurement()?.default) {
            return ":Please fill all measurements";
        }
        return false;
    }, [currentMeasurement()]);

    return (
        <div ref={inputRef} className={`${className} configurator-section__inner-fields-field ${activeCoordinate === key ? "configurator-section__inner-fields-field--active" : ""}`}>
            <InputField
                className={`configurator-measure`}
                value={currentMeasurement()?.default ? null : currentMeasurement().value}
                suffix="Inches"
                onChange={handleChange}
                marker={indexToLetter(data.index)}
                label={name}
                type="number"
                min={minimum || 1}
                max={10000}
                onFocus={() => setActiveCoordinate(key)}
                placeholder={defaultValue}
                onBlur={() => setActiveCoordinate(null)}
                customValidation={customValidation}
            />

            <p className={`base-input__error base-input__label base-input__minimum`}>
                {minimum && (
                    <>
                        Min:
                        {" "}
                        {minimum}
                        ″
                    </>
                )}
            </p>

        </div>
    );
};

const Measurement = ({ file, measurements, product }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [showMeasurementWidget, setShowMeasurementWidget] = useState(false);
    const { setActiveCoordinate, measurements: measurementsValues, measurementsState, priceData } = useContext(PatioContext) || {};
    const [isScrolled, setIsScrolled] = useState(false);

    function closeWidget() {
        setShowMeasurementWidget(false);
        setActiveCoordinate(null);
    };

    function getButtonText() {
        if (measurementsState === MEASUREMENTS_STATE.START) return "Start Here";
        if (measurementsState === MEASUREMENTS_STATE.CONTINUE) return "Continue";
        if (measurementsState === MEASUREMENTS_STATE.FINISHED) return "Edit";
        return "Continue"
    }

    function getFixedButtonText() {
        if (measurementsState === MEASUREMENTS_STATE.START) return "Start Measuring";
        if (measurementsState === MEASUREMENTS_STATE.CONTINUE) return "Continue Measuring";
        if (measurementsState === MEASUREMENTS_STATE.FINISHED) return "Edit";
        return "Continue Measuring"
    }

    useEffect(() => {
        const buttonSnake = document.getElementById("button-snake-mobile");
        const handleScroll = () => {
            setIsScrolled(buttonSnake.getBoundingClientRect().bottom < 0);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    function finishMeasurement() {
        setShowMeasurementWidget(false);
        document.getElementById("fabric-cards").scrollIntoView({ behavior: "smooth", block: "start" });
        setActiveCoordinate(null);
    }

    return (
        <div className="configurator-section configurator-section--measurements">
            <div className="configurator-section__header">
                <h3 className="configurator-section__header-title">1. Measurement</h3>
                {file && (<Link to={file} target="_blank" className="configurator-section__header-link">(How to measure?)</Link>)}

                <div className="button--snake" id="button-snake-mobile">
                    <Button className="configurator-section__header-button" onClick={() => setShowMeasurementWidget(true)}>
                        {getButtonText()}
                        <ArrowRightIcon />
                    </Button>
                </div>
                {!showMeasurementWidget && measurementsState !== MEASUREMENTS_STATE.FINISHED && (
                    <div className={`configurator-section__header-button-wrapper active `}>
                        {priceData?.totalPrice ? (
                            <div className="product-page-patio-summary__inner-price--mobile">
                                <Money value={{ currencyCode: CURRENCY, amount: priceData?.totalPrice }} />
                            </div>
                        ) : null}

                        <Button className="configurator-section__header-button" full onClick={() => setShowMeasurementWidget(true)}>
                            {/* EVEN THO IT SAYS ADD TO CART */}
                            {/* IT WILL OPEN THE MEASUREMENT WIDGET */}
                            {/* AFTER USER FILLS ALL THE MEASUREMENTS */}
                            {/* THIS WILL NOT BE RENDERED AND WILL OPEN ANOTHER BUTTON  */}
                            {/* WHICH WILL BE THE ADD TO CART BUTTON */}
                            Add to Cart
                            {/* <ArrowRightIcon /> */}
                        </Button>
                    </div>
                )}

            </div>

            {showMeasurementWidget && (
                <div onClick={() => closeWidget()} className="measurements-widget__close-button">
                    <CloseIcon />
                </div>
            )}

            <MeasurementsWidget
                product={product}
                file={file}
                show={showMeasurementWidget}
                close={() => finishMeasurement()}
            />

            <div className="configurator-section__inner">
                <div className="configurator-section__inner-fields">
                    {measurements?.map((measurement, index) => (
                        <MeasureInput
                            data={{
                                ...measurement,
                                index,
                            }}
                            measurementIndex={index}
                            key={measurement?.key}
                            className={measurement?.minimum || measurements[index + (index % 2 === 0 ? 1 : -1)]?.minimum ? 'row-has-minimum' : ''}
                        />
                    ))}
                </div>
                <div className="configurator-section__extra-leeway">
                    <img src={infoIcon} alt="Info" />
                    Please enter exact dimensions. To ensure a perfect fit, we’ll add 1-2” leeway to the width and depth. <br />
                    For any curved or angled dimension, find the furthest point and take a straight, level measurement from it.
                </div>
            </div>
        </div>
    );
};

export default Measurement;